import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MoreVertical, Tag as TagIcon, UserPlus, User, UserX, Info, Phone, Mail, MessageSquare, Calendar, FileText, Clock, XCircle, Plus, Trash2, Edit2, Instagram, History } from "lucide-react";
import { ChatWithMessages } from "@/types/chat";
import { useState, useEffect, useRef, useMemo } from "react";
import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useOrgMembersQuery } from "@/hooks/useOrgMembersQuery";
import { supabase, chatSupabase} from '@/integrations/supabase/client';
import { useTags } from "@/hooks/useTags";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { normalizePhone } from "@/utils/phoneUtils";
import { getDefaultAvatar } from "@/utils/avatarUtils";
import { buildOperatorMessagePayload, sendOperatorMessageToWebhook, OPERATOR_MESSAGE_WEBHOOK_URL } from "@/utils/operatorMessageWebhook";
import mixpanel from "mixpanel-browser";

const _isUuid = (v: unknown): boolean =>
  typeof v === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v.trim());

/** Chamado ao encerrar chat do Instagram: preenche closed_at, user_closed_id e notes_closed na tabela chats_instagram */
export type OnCloseChatInstagram = (params: {
  chatId: string;
  userClosedId: string;
  notesClosed: string;
  instanceId?: string;
  orgId?: string;
  origemContato?: string;
}) => void;

interface ChatHeaderProps {
  chat: ChatWithMessages;
  contactTags: any[];
  onUpdateContactTags: (phone: string, tags: any[]) => void;
  onContactAssigned?: () => void;
  onLoadFullHistory?: () => void;
  showAllMessages?: boolean;
  /** Para chats Instagram: ao encerrar, envia dados para preencher no banco (closed_at, user_closed_id, notes_closed) */
  onCloseChatInstagram?: OnCloseChatInstagram;
  /** ID da instância do chat selecionado (fallback para CSAT ao encerrar quando o chat não expõe instance_id) */
  instanceIdFromParent?: string | null;
}

function ChatHeaderComponent({ chat, contactTags, onUpdateContactTags, onContactAssigned, onLoadFullHistory, showAllMessages, onCloseChatInstagram, instanceIdFromParent }: ChatHeaderProps) {
  const [currentContact, setCurrentContact] = useState<any>(null);
  const [loadingAssignment, setLoadingAssignment] = useState(false);
  const [showContactInfo, setShowContactInfo] = useState(false);
  const [assignedToIdOverride, setAssignedToIdOverride] = useState<string | null | undefined>(undefined);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [closeReason, setCloseReason] = useState("");
  const [closeSubreason, setCloseSubreason] = useState("");
  const [closingChat, setClosingChat] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const { currentOrg } = useOrganizationContext();
  const { data: orgMembers = [], isLoading: loadingMembers } = useOrgMembersQuery(currentOrg?.id);
  const [selectedAssigneeId, setSelectedAssigneeId] = useState<string>("");
  const [customFields, setCustomFields] = useState<Record<string, string>>({});
  const [editingField, setEditingField] = useState<string | null>(null);
  const [newFieldName, setNewFieldName] = useState("");
  const [newFieldValue, setNewFieldValue] = useState("");
  const [showAddField, setShowAddField] = useState(false);
  const [savingCustomFields, setSavingCustomFields] = useState(false);
  const [isEditingBasicInfo, setIsEditingBasicInfo] = useState(false);
  const [editingName, setEditingName] = useState("");
  const [editingWhatsApp, setEditingWhatsApp] = useState("");
  const [editingPhone, setEditingPhone] = useState("");
  const [savingBasicInfo, setSavingBasicInfo] = useState(false);
  const { tags, assignTagToContact, removeTagFromContact } = useTags();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const getInitials = (name: string) => {
    if (!name) return "??";
    // Se for um número, retornar apenas os últimos 2 dígitos
    if (/^\d+$/.test(name)) {
      return name.slice(-2);
    }
    // Se for um nome, retornar as iniciais
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase().slice(0, 2);
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Avatar no header: foto do contato (picture_b64) se existir, senão placeholder
  const rawPicB64 = currentContact?.picture_b64;
  // Ignorar se for JSON bruto (resposta da API armazenada por engano)
  const contactPictureB64 = (rawPicB64 && typeof rawPicB64 === 'string' && !rawPicB64.startsWith('{')) ? rawPicB64 : null;
  const headerAvatarSrc = contactPictureB64
    ? (contactPictureB64.startsWith("data:") || contactPictureB64.startsWith("http")
        ? contactPictureB64
        : `data:image/jpeg;base64,${contactPictureB64}`)
    : getDefaultAvatar(chat.phone || chat.id);
  
  const isInstagramChannel = 
    chat.instance?.channel === 'instagram' || 
    chat.instance?.integration_type?.includes('INSTAGRAM') ||
    (chat as any)?.channel === 'instagram';
  // Lista unificada: atribuição em chats_Instagram (user_atribuido) para todos os canais
  const resolvedAssignedToId = (chat as any)?.user_atribuido ?? (chat as any)?.full?.user_atribuido ?? (currentContact as any)?.user_atribuido ?? (currentContact as any)?.assigned_to ?? null;
  const assignedToId = assignedToIdOverride !== undefined ? assignedToIdOverride : resolvedAssignedToId;
  const assignedUserName = useMemo(() => {
    if (!assignedToId) return null;
    return orgMembers.find(m => m.id === assignedToId)?.name ?? null;
  }, [assignedToId, orgMembers]);
  const assignedToMe = assignedToId && user?.id && assignedToId === user.id;
  const instagramUsernameRaw =
    ((currentContact as any)?.User_name_insta || (currentContact as any)?.user_name_insta)
    ?? (chat as any).contactHandle
    ?? (chat as any).full?.contact?.User_name_insta
    ?? (chat as any).full?.contact?.user_name_insta;
  const instagramUsername = instagramUsernameRaw?.trim()
    ? `@${String(instagramUsernameRaw).trim().replace(/^@/, '')}`
    : undefined;

  // Nome para exibição: priorizar currentContact, depois dados do próprio chat (adapter/full) para Instagram
  const displayName = (() => {
    const fromContact = currentContact?.name &&
      currentContact.name.trim() !== '' &&
      currentContact.name !== chat.phone &&
      normalizePhone(currentContact.name) !== normalizePhone(chat.phone || '')
        ? currentContact.name
        : null;
    if (fromContact) return fromContact;
    const c = chat as any;
    const fromChat = c.contactName ?? c.contact?.name ?? c.full?.contact?.name ?? c.full?.name ?? c.name;
    if (fromChat && String(fromChat).trim() !== '') return String(fromChat).trim();
    return chat.phone || "Contato desconhecido";
  })();

  // Contato para exibição no modal: currentContact ou contato que veio no chat (Instagram)
  const contactForDisplay = currentContact ?? (chat as any).full?.contact ?? (chat as any).contact;

  // Estado para verificar se há novas mensagens após o encerramento
  const [hasNewMessagesAfterClose, setHasNewMessagesAfterClose] = useState(false);

  // Refs para evitar múltiplas execuções
  const lastFetchedPhoneRef = useRef<string | null>(null);
  const contactChannelRef = useRef<any>(null);

  // Identificador do contato: phone (WhatsApp) ou origem_contato/Account_ID_insta (Instagram)
  const phoneOrAccountId = String(
    chat.phone ?? (chat as any).full?.origem_contato ?? (chat as any).origem_contato ?? ''
  ).trim();
  const contactIdFromChat =
    (chat as any).contactId ??
    (chat as any).full?.contact_id ??
    (chat as any).contact_id;

  // Instagram: usar contato que já veio no chat (do useInstagramChats) enquanto o fetch do header não retorna
  useEffect(() => {
    if (!isInstagramChannel) return;
    const c = chat as any;
    const contactFromChat = c.full?.contact ?? c.contact;
    if (contactFromChat && !currentContact) {
      const withInstagram = {
        ...contactFromChat,
        Account_ID_insta: contactFromChat.Account_ID_insta ?? contactFromChat.account_id_insta ?? contactFromChat.instagram_account_id,
        User_name_insta: contactFromChat.User_name_insta ?? contactFromChat.user_name_insta,
      };
      setCurrentContact(withInstagram);
    }
  }, [isInstagramChannel, chat, currentContact]);

  // Buscar contato pelo telefone ou Account_ID_insta - SEMPRE do banco quando a página carregar
  useEffect(() => {
    if (!phoneOrAccountId || !currentOrg?.id || !user) {
      setCurrentContact(null);
      setHasNewMessagesAfterClose(false);
      return;
    }

    // Só buscar se o identificador realmente mudou
    if (lastFetchedPhoneRef.current === phoneOrAccountId) {
      return;
    }

    lastFetchedPhoneRef.current = phoneOrAccountId;

    const fetchContact = async () => {
      try {
        const normalizedPhone = normalizePhone(phoneOrAccountId);

        const isInstagram =
          chat.instance?.channel === 'instagram' ||
          chat.instance?.integration_type?.includes('INSTAGRAM') ||
          (chat as any)?.channel === 'instagram';

        // Preferir cache já populado pelo useInstagramChats antes de ir ao banco
        const cachedContacts = queryClient.getQueryData<any[]>(["contacts-unified", currentOrg.id]) ?? [];

        let allContacts: any[] = cachedContacts;

        if (!allContacts.length) {
          // Cache vazio: busca direcionada (filtra no banco, não full-scan)
          const CONTACT_FIELDS_HEADER = 'id, org_id, name, email, whatsapp, phone, notes, created_at, updated_at, instance_id, custom_fields, visitor_id, metadata, Account_ID_insta, User_name_insta';
          const orFilter = isInstagram
            ? `Account_ID_insta.eq.${phoneOrAccountId},phone.eq.${phoneOrAccountId},id.eq.${contactIdFromChat ?? 'null'}`
            : `phone.eq.${phoneOrAccountId},whatsapp.eq.${phoneOrAccountId},phone.eq.${normalizedPhone},whatsapp.eq.${normalizedPhone}`;
          const { data, error } = await supabase
            .from('contacts')
            .select(CONTACT_FIELDS_HEADER)
            .eq('org_id', currentOrg.id)
            .or(orFilter)
            .limit(5);
          if (error && error.code !== 'PGRST116') return;
          allContacts = data ?? [];
        }

        // Encontrar contato: para Instagram, priorizar Account_ID_insta; para outros, comparar telefone normalizado
        const contactById = isInstagram && contactIdFromChat
          ? allContacts.find((c: any) => c.id === contactIdFromChat)
          : undefined;
        const contactByAccountId = isInstagram
          ? allContacts.find((c: any) => {
              const id = String(c.Account_ID_insta ?? c.account_id_insta ?? c.instagram_account_id ?? '');
              return id !== '' && id === String(phoneOrAccountId);
            })
          : undefined;
        const contactByPhone = isInstagram
          ? allContacts.find((c: any) => c.phone === phoneOrAccountId)
          : allContacts.find((c: any) => {
              const contactNormalized = normalizePhone(c.phone);
              return contactNormalized === normalizedPhone && contactNormalized.length > 0;
            });
        const contact = contactById || contactByAccountId || contactByPhone;

        if (contact) {
          // Verificar se os dados estão nos custom_fields também
          const customFields = (contact as any).custom_fields;
          const instagramData = customFields && typeof customFields === 'object' ? customFields : {};
          
          const contactWithInstagram = {
            ...contact,
            Account_ID_insta: contact.Account_ID_insta || instagramData.Account_ID_insta,
            User_name_insta: contact.User_name_insta ?? instagramData.User_name_insta
          };
          
          setCurrentContact(contactWithInstagram);
          
          // Carregar campos customizados
          if ((contact as any).custom_fields && typeof (contact as any).custom_fields === 'object') {
            setCustomFields((contact as any).custom_fields || {});
          } else {
            setCustomFields({});
          }
          
        setAssignedToIdOverride(undefined);

          // Verificar se há mensagens após o encerramento (closed_at fica em chats_Instagram, não em contacts)
          const closedAt = (chat as any)?.closed_at ?? (chat as any)?.full?.closed_at ?? null;
          if (closedAt) {
            const { data: orgInstances } = await supabase
              .from('instances')
              .select('id')
              .eq('org_id', currentOrg.id)
              .eq('status', 'open');
            const instanceIds = orgInstances?.map((i: any) => i.id) || [];
            if (instanceIds.length > 0) {
              const { chatId } = getInstagramChatMeta();
              if (chatId) {
                const { data: messagesAfterClose } = await chatSupabase
                  .from('messages')
                  .select('message_id, created_at')
                  .eq('chat_id', chatId)
                  .gt('created_at', closedAt)
                  .limit(1);
                setHasNewMessagesAfterClose((messagesAfterClose?.length || 0) > 0);
              } else {
                setHasNewMessagesAfterClose(false);
              }
            } else {
              setHasNewMessagesAfterClose(false);
            }
          } else {
            setHasNewMessagesAfterClose(false);
          }
        } else {
          // Instagram: não zerar currentContact se o chat já trouxe contato (useInstagramChats)
          const contactFromChat = (chat as any).full?.contact ?? (chat as any).contact;
          if (!isInstagram || !contactFromChat) {
            setCurrentContact(null);
          }
          setHasNewMessagesAfterClose(false);
          if (!isInstagram || !contactFromChat) setCustomFields({});
          setAssignedToIdOverride(undefined);
        }
      } catch (error) {
        console.error('Erro ao buscar contato:', error);
      }
    };

    fetchContact();

    // Subscription em tempo real para atualizar contato quando houver mudanças
    if (contactChannelRef.current) {
      supabase.removeChannel(contactChannelRef.current);
    }

    const normalizedPhone = normalizePhone(chat.phone);
    contactChannelRef.current = supabase
      .channel(`contact-${chat.phone}-${currentOrg.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'chat',
          table: 'contacts',
          filter: `org_id=eq.${currentOrg.id}`,
        },
        (payload) => {
          const updatedContact = payload.new as any;
          const contactNormalized = normalizePhone(updatedContact.phone);
          if (contactNormalized === normalizedPhone) {
            setCurrentContact(updatedContact);
            // Atualizar hasNewMessagesAfterClose se necessário
            if (updatedContact.closed_at) {
              // Verificar mensagens após encerramento
              supabase
                .from('instances')
                .select('id')
                .eq('org_id', currentOrg.id)
                .eq('status', 'open')
                .then(({ data: orgInstances }) => {
                  const instanceIds = orgInstances?.map(i => i.id) || [];
                  if (instanceIds.length > 0) {
                    const { chatId } = getInstagramChatMeta();
                    if (chatId) {
                      chatSupabase
                        .from('messages')
                        .select('message_id, created_at')
                        .eq('chat_id', chatId)
                        .gt('created_at', updatedContact.closed_at)
                        .limit(1)
                        .then(({ data: messagesAfterClose }) => {
                          setHasNewMessagesAfterClose((messagesAfterClose?.length || 0) > 0);
                        });
                    } else {
                      setHasNewMessagesAfterClose(false);
                    }
                  }
                });
            } else {
              setHasNewMessagesAfterClose(false);
            }
          }
        }
      )
      .subscribe();
    
    return () => {
      if (contactChannelRef.current) {
        supabase.removeChannel(contactChannelRef.current);
        contactChannelRef.current = null;
      }
    };
  }, [phoneOrAccountId, currentOrg?.id, user?.id, chat.instance?.channel, (chat as any)?.channel]); // Incluir phoneOrAccountId e channel para Instagram

  useEffect(() => {
    if (!assignDialogOpen || !orgMembers.length || selectedAssigneeId) return;
    const first = orgMembers.find((m) => m.id !== user?.id) || orgMembers[0];
    setSelectedAssigneeId(first?.id || "");
  }, [assignDialogOpen, orgMembers, user?.id, selectedAssigneeId]);

  const getInstagramChatMeta = () => {
    const raw: any = chat as any;
    const instanceId = raw?.instance_id ?? raw?.full?.instance_id ?? chat.instance_id;
    const origemContato = raw?.phone ?? raw?.full?.origem_contato;
    // Em alguns canais, chat_id não é UUID puro.
    // Priorizar chat_id explícito; se não vier, tentar extrair do id da lista "instance_id:chat_id".
    const candidate = raw?.chat_id ?? raw?.full?.chat_id ?? null;
    const listId = typeof raw?.id === 'string' ? raw.id : null;
    const extractedFromListId =
      !candidate && listId && listId.includes(':')
        ? listId.split(':').slice(1).join(':')
        : null;
    const chatId = (typeof candidate === 'string' && candidate.trim() !== '')
      ? candidate.trim()
      : (typeof extractedFromListId === 'string' && extractedFromListId.trim() !== '')
        ? extractedFromListId.trim()
        : null;
    return { chatId, instanceId, origemContato };
  };

  const insertInstagramSystemMessage = async (content: string) => {
    const { chatId, instanceId, origemContato } = getInstagramChatMeta();
    if (!chatId || !instanceId || !origemContato) return;
    await chatSupabase.from('messages').insert({
      chat_id: chatId,
      instance_id: instanceId,
      origem_contato: origemContato,
      content_message: content,
      message_type: 'text',
      user_sender: 'Sistema',
      is_internal: false,
      created_at: new Date().toISOString(),
    });
  };

  const handleAssignToUser = async (targetUserId: string) => {
    if (!user || !currentOrg?.id || !chat.phone || !targetUserId) return;
    setLoadingAssignment(true);
    try {
      if (isInstagramChannel) {
        const now = new Date();
        const { chatId, instanceId, origemContato } = getInstagramChatMeta();
        const resolvedContactId =
          (chat as any)?.contactId ??
          (chat as any)?.full?.contact_id ??
          (chat as any)?.contact_id ??
          (currentContact as any)?.id ??
          null;
        if (!instanceId) {
          toast.error('Chat sem instance_id.');
          setLoadingAssignment(false);
          return;
        }
        const { data: assigneeProfile } = await supabase
          .from('profiles')
          .select('name, email')
          .eq('id', targetUserId)
          .maybeSingle();
        const assigneeName = assigneeProfile?.name || assigneeProfile?.email || 'um operador';
        const origem = origemContato ?? chat.phone ?? null;
        const nextMessage = `Este chat foi atribuído para ${assigneeName} em ${format(now, "dd/MM/yyyy", { locale: ptBR })} às ${format(now, "HH:mm", { locale: ptBR })}`;
        if (chatId) {
          const { error } = await chatSupabase.from('conversations')
            .upsert({
              chat_id: chatId,
              instance_id: instanceId,
              org_id: currentOrg.id,
              origem_contato: origem,
              contact_id: resolvedContactId,
              user_atribuido: targetUserId,
              momento_atribuido: now.toISOString(),
            }, { onConflict: 'chat_id' });
          if (error) throw error;
          {
            const { syncChatSessionOnAssign } = await import('@/lib/chatSessionSync');
            syncChatSessionOnAssign(currentOrg.id, 'instagram', chatId, {
              assigned_to: targetUserId,
              assigned_at: now.toISOString(),
            }).catch(() => {});
          }
          if (origem) await insertInstagramSystemMessage(nextMessage);
        } else if (origem) {
          const { error: updateErr } = await chatSupabase.from('conversations')
            .update({
              user_atribuido: targetUserId,
              momento_atribuido: now.toISOString(),
            })
            .eq('instance_id', instanceId)
            .eq('origem_contato', origem)
            .eq('org_id', currentOrg.id);
          if (updateErr) console.warn('Erro ao atualizar atribuição (instance/origem):', updateErr);
        }
        supabase.rpc('insert_user_event', {
          p_event_type: 'chat_assigned',
          p_org_id:     currentOrg.id,
          p_user_id:    user.id,
          p_metadata:   {
            assigned_to_id:   targetUserId,
            assigned_to_name: assigneeName,
            assigned_by_id:   user.id,
            chat_id:          chatId ?? null,
          },
        }).then(({ error }) => { if (error) console.error('[events] chat_assigned:', error) });
        toast.success('Contato atribuído com sucesso!');
        setAssignedToIdOverride(targetUserId || null);
        setAssignDialogOpen(false);
        setTimeout(() => onContactAssigned?.(), 150);
        setLoadingAssignment(false);
        return;
      }

      const normalizedPhone = normalizePhone(chat.phone);

      // Atribuição unificada em chats_Instagram (não em contacts)
      const { chatId, instanceId, origemContato } = getInstagramChatMeta();
      const resolvedContactId =
        (chat as any)?.contactId ??
        (chat as any)?.full?.contact_id ??
        (chat as any)?.contact_id ??
        (currentContact as any)?.id ??
        null;
      if (chatId && instanceId) {
        const { data: existingRow } = await chatSupabase
          .from('conversations')
          .select('user_atribuido')
          .eq('chat_id', chatId)
          .maybeSingle();
        if ((existingRow as any)?.user_atribuido === targetUserId) {
          toast.info('Este chat já está atribuído para este usuário.');
          onContactAssigned?.();
          return;
        }
      }

      const { data: assigneeProfile } = await supabase
        .from('profiles')
        .select('name, email')
        .eq('id', targetUserId)
        .maybeSingle();
      const assigneeName = assigneeProfile?.name || assigneeProfile?.email || 'um operador';
      const now = new Date();
      const formattedDate = format(now, "dd/MM/yyyy", { locale: ptBR });
      const formattedTime = format(now, "HH:mm", { locale: ptBR });
      const nextMessage = `Este chat foi atribuído para ${assigneeName} em ${formattedDate} às ${formattedTime}`;

      // Lista unificada: atribuição em chats_Instagram (chat_id pode ser UUID ou outro formato por canal)
      if (instanceId) {
        const origem = origemContato ?? normalizedPhone ?? chat.phone ?? null;
        if (chatId) {
          const payload = {
            chat_id: chatId,
            instance_id: instanceId,
            org_id: currentOrg.id,
            origem_contato: origem,
            contact_id: resolvedContactId,
            user_atribuido: targetUserId,
            momento_atribuido: now.toISOString(),
          };
          const { data: updatedByChatId, error: updateByChatIdErr } = await chatSupabase
            .from('conversations')
            .update({
              user_atribuido: targetUserId,
              momento_atribuido: now.toISOString(),
            })
            .eq('chat_id', chatId)
            .eq('instance_id', instanceId)
            .eq('org_id', currentOrg.id)
            .select('chat_id')
            .limit(1);
          let persistError: any = updateByChatIdErr;

          // Se não encontrou linha para UPDATE, faz upsert da conversa.
          if (!updatedByChatId && !updateByChatIdErr) {
            const { error: upsertChatError } = await chatSupabase
              .from('conversations')
              .upsert(payload as any, { onConflict: 'chat_id' });
            persistError = upsertChatError;
          }

          if (persistError) {
            throw persistError;
          }

          {
            const { syncChatSessionOnAssign } = await import('@/lib/chatSessionSync');
            syncChatSessionOnAssign(currentOrg.id, 'instagram', chatId, {
              assigned_to: targetUserId,
              assigned_at: now.toISOString(),
            }).catch(() => {});
          }
          if (origem) {
            await chatSupabase.from('messages').insert({
              chat_id: chatId,
              instance_id: instanceId,
              origem_contato: origem,
              content_message: nextMessage,
              message_type: 'text',
              user_sender: 'Sistema',
              is_internal: false,
              created_at: now.toISOString(),
            });
          }
        } else if (origem) {
          const { error: updateError } = await chatSupabase.from('conversations')
            .update({
              user_atribuido: targetUserId,
              momento_atribuido: now.toISOString(),
            })
            .eq('instance_id', instanceId)
            .eq('origem_contato', origem)
            .eq('org_id', currentOrg.id);
          if (updateError) throw updateError;
        }
      }

      supabase.rpc('insert_user_event', {
        p_event_type: 'chat_assigned',
        p_org_id:     currentOrg.id,
        p_user_id:    user.id,
        p_metadata:   {
          assigned_to_id:   targetUserId,
          assigned_to_name: assigneeName,
          assigned_by_id:   user.id,
          chat_id:          chatId ?? null,
        },
      }).then(({ error }) => { if (error) console.error('[events] chat_assigned:', error) });

      setCurrentContact({
        ...(currentContact as any),
        phone: chat.phone,
        org_id: currentOrg.id,
        user_atribuido: targetUserId,
        assigned_to: targetUserId,
      });

      toast.success('Contato atribuído com sucesso!');
      setAssignedToIdOverride(targetUserId || null);
      onContactAssigned?.();
      setAssignDialogOpen(false);
    } catch (error) {
      console.error('Erro ao atribuir contato:', error);
      toast.error('Erro ao atribuir contato. Tente novamente.');
    } finally {
      setLoadingAssignment(false);
    }
  };

  // Função para atribuir contato para si mesmo
  const handleAssignToMe = async () => {
    await handleAssignToUser(user?.id || "");
  };

  // Função para desatribuir contato
  const handleUnassign = async () => {
    if (!user || !currentOrg?.id || !chat.phone) return;

    setLoadingAssignment(true);

    try {
      if (isInstagramChannel) {
        const previousAssigneeId = assignedToIdOverride !== undefined
          ? assignedToIdOverride
          : resolvedAssignedToId;
        const { chatId, instanceId, origemContato } = getInstagramChatMeta();
        if (!instanceId) {
          toast.error('Chat sem instance_id.');
          setLoadingAssignment(false);
          return;
        }
        const origem = origemContato ?? chat.phone ?? null;
        if (chatId) {
          const { error } = await chatSupabase.from('conversations')
            .upsert({
              chat_id: chatId,
              instance_id: instanceId,
              org_id: currentOrg.id,
              origem_contato: origem,
              user_atribuido: null,
              momento_atribuido: null,
            }, { onConflict: 'chat_id' });
          if (error) throw error;
        } else if (origem) {
          const { error: updateErr } = await chatSupabase.from('conversations')
            .update({
              user_atribuido: null,
              momento_atribuido: null,
            })
            .eq('instance_id', instanceId)
            .eq('origem_contato', origem)
            .eq('org_id', currentOrg.id);
          if (updateErr) console.warn('Erro ao desatribuir (instance/origem):', updateErr);
        }
        if (chatId && origem) {
          let unassignedName = 'Usuário';
          if (previousAssigneeId) {
            const { data: assigneeProfile } = await supabase
              .from('profiles')
              .select('name, email')
              .eq('id', previousAssigneeId)
              .maybeSingle();
            unassignedName = assigneeProfile?.name || assigneeProfile?.email || unassignedName;
          } else {
            const { data: userProfile } = await supabase
              .from('profiles')
              .select('name, email')
              .eq('id', user.id)
              .maybeSingle();
            unassignedName = userProfile?.name || userProfile?.email || unassignedName;
          }
          const now = new Date();
          const formattedDate = format(now, "dd/MM/yyyy", { locale: ptBR });
          const formattedTime = format(now, "HH:mm", { locale: ptBR });
          await insertInstagramSystemMessage(
            `Este chat foi desatribuído de ${unassignedName} em ${formattedDate} às ${formattedTime}`
          );
        }
        setAssignedToIdOverride(null);
        toast.success('Chat desatribuído com sucesso!');
        setTimeout(() => onContactAssigned?.(), 150);
        setLoadingAssignment(false);
        return;
      }

      // Desatribuição unificada: atualizar apenas chats_Instagram (não contacts)
      const { chatId, instanceId, origemContato } = getInstagramChatMeta();
      const previousAssigneeId = assignedToIdOverride ?? resolvedAssignedToId ?? null;
      const origem = origemContato ?? chat.phone ?? null;
      if (!instanceId || (!chatId && !origem)) {
        toast.error('Não foi possível identificar o chat para desatribuir.');
        setLoadingAssignment(false);
        return;
      }

      const byChatId = !!chatId;
      const conversations = chatSupabase.from('conversations');
      const { data: row } = byChatId
        ? await conversations
            .select('user_atribuido')
            .eq('chat_id', chatId)
            .maybeSingle()
        : await conversations
            .select('user_atribuido')
            .eq('instance_id', instanceId)
            .eq('origem_contato', origem)
            .eq('org_id', currentOrg.id)
            .maybeSingle();
      if (!(row as any)?.user_atribuido) {
        toast.warning('Este chat não está atribuído.');
        setLoadingAssignment(false);
        return;
      }

      if (byChatId) {
        const { error: updateError } = await conversations
          .update({ user_atribuido: null, momento_atribuido: null })
          .eq('chat_id', chatId);
        if (updateError) throw updateError;
      } else {
        const { error: updateError } = await conversations
          .update({ user_atribuido: null, momento_atribuido: null })
          .eq('instance_id', instanceId)
          .eq('origem_contato', origem)
          .eq('org_id', currentOrg.id);
        if (updateError) throw updateError;
      }

      let unassignedName = 'Usuário';
      if (previousAssigneeId) {
        const { data: assigneeProfile } = await supabase
          .from('profiles')
          .select('name, email')
          .eq('id', previousAssigneeId)
          .maybeSingle();
        unassignedName = assigneeProfile?.name || assigneeProfile?.email || unassignedName;
      } else {
        const { data: userProfile } = await supabase
          .from('profiles')
          .select('name, email')
          .eq('id', user.id)
          .maybeSingle();
        unassignedName = userProfile?.name || userProfile?.email || unassignedName;
      }
      const now = new Date();
      const formattedDate = format(now, "dd/MM/yyyy", { locale: ptBR });
      const formattedTime = format(now, "HH:mm", { locale: ptBR });
      const systemMessage = `Este chat foi desatribuído de ${unassignedName} em ${formattedDate} às ${formattedTime}`;
      const normalizedPhone = (() => {
        const p = chat.phone;
        if (!p) return '';
        let n = p.replace(/\D/g, '');
        if (n.length === 13 && n[4] === '9') n = n.slice(0, 4) + n.slice(5);
        return n;
      })();

      if (chatId) {
        await chatSupabase.from('messages').insert({
          chat_id: chatId,
          instance_id: instanceId,
          origem_contato: origemContato ?? normalizedPhone ?? chat.phone,
          content_message: systemMessage,
          message_type: 'text',
          user_sender: 'Sistema',
          is_internal: false,
          created_at: now.toISOString(),
        });
      }

      const { chatId: metaChatId, instanceId: metaInstanceId, origemContato: metaOrigem } = getInstagramChatMeta();
      if (metaChatId && metaInstanceId && metaOrigem) {
        await chatSupabase.from('messages').insert({
          chat_id: metaChatId,
          instance_id: metaInstanceId,
          origem_contato: metaOrigem,
          content_message: systemMessage,
          message_type: 'text',
          user_sender: 'Sistema',
          is_internal: false,
          created_at: now.toISOString(),
        });
      }

      setAssignedToIdOverride(null);
      setCurrentContact(null);
      toast.success('Chat desatribuído com sucesso!');
      
      // Notificar que o contato foi desatribuído para atualizar a lista
      if (onContactAssigned) {
        onContactAssigned();
      }
    } catch (error) {
      console.error('Erro ao desatribuir contato:', error);
      toast.error('Erro ao desatribuir contato. Tente novamente.');
    } finally {
      setLoadingAssignment(false);
    }
  };

  // Função para alternar tag
  const handleTagToggle = async (tagId: string, checked: boolean) => {
    if (!currentContact || !chat.phone) return;
    mixpanel.track('app_chats_tag_toggled', { action: checked ? 'added' : 'removed' })

    try {
      if (checked) {
        // Verificar se a tag já existe antes de adicionar
        const tagAlreadyExists = contactTags.some(tag => tag.id === tagId);
        if (tagAlreadyExists) {
          console.log('Tag já existe para este contato');
          return;
        }

        await assignTagToContact.mutateAsync({ contactId: currentContact.id, tagId });
        
        // Buscar informações da tag e do usuário para criar mensagem de sistema
        const tag = tags?.find(t => t.id === tagId);
        if (tag && chat.instance_id) {
          // Buscar nome do usuário
          const { data: userProfile } = await supabase
            .from('profiles')
            .select('name')
            .eq('id', user.id)
            .single();

          const userName = userProfile?.name || user?.email || 'Usuário';
          const now = new Date();
          const formattedDate = format(now, "dd/MM/yyyy", { locale: ptBR });
          const formattedTime = format(now, "HH:mm", { locale: ptBR });

          const { chatId: tagChatId, instanceId: tagInstanceId, origemContato: tagOrigem } = getInstagramChatMeta();
          if (tagChatId && tagInstanceId && tagOrigem) {
            const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();
            const { data: recentTagMessage } = await chatSupabase
              .from('messages')
              .select('message_id')
              .eq('chat_id', tagChatId)
              .like('content_message', `%tag "${tag.name}"%`)
              .gte('created_at', fiveMinutesAgo)
              .limit(1);

            if (!recentTagMessage || recentTagMessage.length === 0) {
              const systemMessage = `Tag "${tag.name}" foi adicionada por ${userName} em ${formattedDate} às ${formattedTime}`;
              await chatSupabase.from('messages').insert({
                chat_id: tagChatId,
                instance_id: tagInstanceId,
                origem_contato: tagOrigem,
                content_message: systemMessage,
                message_type: 'text',
                user_sender: 'Sistema',
                is_internal: false,
                created_at: now.toISOString(),
              });
            }
          }
        }
        
        // Adicionar tag localmente
        if (tag) {
          const newTags = [...contactTags, tag];
          onUpdateContactTags(chat.phone, newTags);
        }
      } else {
        await removeTagFromContact.mutateAsync({ contactId: currentContact.id, tagId });
        
        // Buscar informações da tag removida para criar mensagem de sistema
        const tag = tags?.find(t => t.id === tagId);
        if (tag && chat.instance_id) {
          // Buscar nome do usuário
          const { data: userProfile } = await supabase
            .from('profiles')
            .select('name')
            .eq('id', user.id)
            .single();

          const userName = userProfile?.name || user?.email || 'Usuário';
          const now = new Date();
          const formattedDate = format(now, "dd/MM/yyyy", { locale: ptBR });
          const formattedTime = format(now, "HH:mm", { locale: ptBR });

          const { chatId: tagChatId, instanceId: tagInstanceId, origemContato: tagOrigem } = getInstagramChatMeta();
          if (tagChatId && tagInstanceId && tagOrigem) {
            const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();
            const { data: recentTagMessage } = await chatSupabase
              .from('messages')
              .select('message_id')
              .eq('chat_id', tagChatId)
              .like('content_message', `%tag "${tag.name}" foi removida%`)
              .gte('created_at', fiveMinutesAgo)
              .limit(1);

            if (!recentTagMessage || recentTagMessage.length === 0) {
              const systemMessage = `Tag "${tag.name}" foi removida por ${userName} em ${formattedDate} às ${formattedTime}`;
              await chatSupabase.from('messages').insert({
                chat_id: tagChatId,
                instance_id: tagInstanceId,
                origem_contato: tagOrigem,
                content_message: systemMessage,
                message_type: 'text',
                user_sender: 'Sistema',
                is_internal: false,
                created_at: now.toISOString(),
              });
            }
          }
        }
        
        // Remover tag localmente
        const newTags = contactTags.filter(t => t.id !== tagId);
        onUpdateContactTags(chat.phone, newTags);
      }
    } catch (error) {
      console.error('Erro ao alterar tag:', error);
    }
  };

  // Função para encerrar chat
  const handleCloseChat = async () => {
    mixpanel.track('app_chats_close_confirmed')
    console.log('[handleCloseChat] Chamado', {
      closeReason: closeReason.trim(),
      closeSubreason: closeSubreason.trim(),
      hasUser: !!user,
      orgId: currentOrg?.id,
      chatPhone: chat.phone,
      chatId: (chat as any).chat_id ?? (chat as any).id,
      instanceId: (chat as any).instance_id ?? instanceIdFromParent,
    });

    if (!closeReason.trim() || !closeSubreason.trim() || !user || !currentOrg?.id) {
      toast.error('Preencha o motivo e submotivo para encerrar o chat.');
      return;
    }

    setClosingChat(true);

    // Capturar instância/chat e motivo antes do try para usar no finally (CSAT/webhook)
    const rawChatSnap = chat as any;
    const snapCloseReason = closeReason.trim();
    const snapCloseSubreason = closeSubreason.trim();
    const snapInstanceId: string =
      (rawChatSnap?.instance_id ? String(rawChatSnap.instance_id).trim() : '') ||
      (rawChatSnap?.full?.instance_id ? String(rawChatSnap.full.instance_id).trim() : '') ||
      (chat.instance?.id ? String(chat.instance.id).trim() : '') ||
      (instanceIdFromParent ? String(instanceIdFromParent).trim() : '');
    const snapChatId: string =
      (rawChatSnap?.chat_id ? String(rawChatSnap.chat_id).trim() : '') ||
      (rawChatSnap?.full?.chat_id ? String(rawChatSnap.full?.chat_id).trim() : '') ||
      (rawChatSnap?.id ? String(rawChatSnap.id).trim() : '') ||
      (chat.id ? String(chat.id).trim() : '');
    const snapPhone: string =
      (chat.phone ? String(chat.phone).trim() : '') ||
      (rawChatSnap?.full?.origem_contato ? String(rawChatSnap.full.origem_contato).trim() : '');
    const snapOrgId = currentOrg!.id;
    const snapUserId = user?.id ?? null;

    let closeSucceeded = false;
    let closedAtIso = '';

    try {
      // Instagram: preencher closed_at, user_closed_id e notes_closed na tabela chats_instagram
      if (isInstagramChannel && onCloseChatInstagram) {
        const chatId = (chat as any).id ?? (chat as any).full?.chat_id;
        if (chatId) {
          onCloseChatInstagram({
            chatId,
            userClosedId: user.id,
            notesClosed: `${closeReason.trim()}\n${closeSubreason.trim()}`,
            instanceId: (chat as any).instance_id ?? (chat as any).full?.instance_id,
            orgId: (chat as any).full?.org_id ?? currentOrg?.id,
            origemContato: (chat as any).phone ?? (chat as any).full?.origem_contato,
          });
        }
      }

      closedAtIso = new Date().toISOString();
      const { chatId, instanceId, origemContato } = getInstagramChatMeta();

      // Encerramento: sempre atualizar chats_Instagram (fonte da conversa); o chat é exibido por estar ali, não por existir em contacts
      const closePayload = {
        closed_at: closedAtIso,
        user_closed_id: user.id,
        notes_closed: [closeReason.trim(), closeSubreason.trim()].filter(Boolean).join(' – ') || null,
        user_atribuido: null,
        momento_atribuido: null,
      };
      let chatUpdated = false;
      if (chatId && instanceId) {
        const { data: updatedRows, error: chatUpdateError } = await chatSupabase.from('conversations')
          .update(closePayload)
          .eq('chat_id', chatId)
          .select('chat_id');
        if (chatUpdateError) {
          console.warn('Erro ao atualizar conversations (encerramento):', chatUpdateError);
          toast.error('Erro ao encerrar chat. Tente novamente.');
          setClosingChat(false);
          return;
        }
        chatUpdated = Array.isArray(updatedRows) && updatedRows.length > 0;
        if (chatUpdated) {
          const { syncChatSessionOnClose } = await import('@/lib/chatSessionSync');
          syncChatSessionOnClose(currentOrg.id, 'instagram', chatId, {
            closed_at: closedAtIso,
            closed_by: user.id,
          }).catch(() => {});
        }
      }
      // Fallback: update por instance_id + origem_contato quando chatId não bateu nenhuma linha
      if (!chatUpdated && instanceId && (origemContato ?? chat.phone)) {
        const { data: fallbackRows, error: updateErr } = await chatSupabase.from('conversations')
          .update(closePayload)
          .eq('instance_id', instanceId)
          .eq('origem_contato', origemContato ?? chat.phone)
          .eq('org_id', currentOrg.id)
          .select('chat_id');
        if (updateErr) console.warn('Erro ao atualizar conversations (encerramento por instance/origem):', updateErr);
        chatUpdated = Array.isArray(fallbackRows) && fallbackRows.length > 0;
      }
      if (!chatUpdated) {
        console.warn('[closeChat] Nenhuma linha atualizada no banco — chatId:', chatId, 'instanceId:', instanceId);
        toast.error('Erro ao encerrar chat: conversa não encontrada. Tente novamente.');
        setClosingChat(false);
        return;
      }

      // Opcional: atualizar updated_at em contacts se o contato existir (apenas campos permitidos)
      const CONTACT_FIELDS = 'id, org_id, name, email, whatsapp, phone, notes, created_at, updated_at, instance_id, custom_fields, visitor_id, metadata, Account_ID_insta, User_name_insta';
      const normalizedPhone = normalizePhone(chat.phone);
      const { data: allContacts } = await supabase
        .from('contacts')
        .select(CONTACT_FIELDS)
        .eq('org_id', currentOrg.id);
      const contact = allContacts?.find((c: any) => {
        if (isInstagramChannel) return c.Account_ID_insta === chat.phone || normalizePhone(c.phone) === normalizedPhone;
        return normalizePhone(c.phone) === normalizedPhone && normalizedPhone.length > 0;
      });
      if (contact) {
        await supabase.from('contacts').update({ updated_at: closedAtIso }).eq('id', contact.id);
        const { data: updatedContact } = await supabase.from('contacts').select(CONTACT_FIELDS).eq('id', contact.id).single();
        if (updatedContact) setCurrentContact(updatedContact);
      }

      closeSucceeded = true;

      supabase.rpc('insert_user_event', {
        p_event_type: 'chat_closed',
        p_org_id:     snapOrgId,
        p_user_id:    snapUserId,
        p_metadata:   {
          chat_id:    snapChatId || null,
          phone:      snapPhone || null,
          reason:     snapCloseReason,
          subreason:  snapCloseSubreason,
        },
      }).then(({ error }) => { if (error) console.error('[events] chat_closed:', error) });

      toast.success('Chat encerrado com sucesso!');
      setShowCloseDialog(false);
      setCloseReason("");
      setCloseSubreason("");
      if (onContactAssigned) onContactAssigned();
    } catch (error) {
      console.error('Erro ao encerrar chat:', error);
      toast.error('Erro ao encerrar chat. Tente novamente.');
    } finally {
      setClosingChat(false);

      // ─── CSAT: enviar pesquisa ao encerrar se flag_csat ativo ───
      // Roda no finally para nunca ser bloqueado pelo fluxo principal
      if (closeSucceeded && snapInstanceId) {
        (async () => {
          try {
            console.log('[CSAT] Verificando pesquisa para instância', snapInstanceId);
            const { data: csatInstance, error: csatFetchErr } = await supabase
              .from('instances')
              .select('*')
              .eq('id', snapInstanceId)
              .maybeSingle();
            if (csatFetchErr) console.warn('[CSAT] Erro ao buscar instância:', csatFetchErr);

            console.log('[CSAT] flag_csat =', (csatInstance as any)?.flag_csat, '| instância:', csatInstance);

            if (csatInstance && (csatInstance as any).flag_csat) {
              const csatLinkId = snapChatId || (snapInstanceId && snapPhone ? `${snapInstanceId}:${snapPhone}` : snapInstanceId);
              const instId = String((csatInstance as any).id ?? "");
              const resolvedChannel = (csatInstance as any).channel ?? (instId.toLowerCase().startsWith("messenger") ? "messenger" : instId.toLowerCase().startsWith("instagram") ? "instagram" : null);
              const surveyUrl = `https://www.fluity.com.br/avaliacao/${encodeURIComponent(csatLinkId)}`;
              const surveyMessage = `Que bom que você foi atendido pelo nosso time 😊\n\nPara continuarmos melhorando nosso atendimento, pedimos que, se possível, responda rapidamente à nossa pesquisa de satisfação no link abaixo:\n\n${surveyUrl}\n\nSua opinião é muito importante para entendermos como estamos atendendo você e onde podemos melhorar.\n\n*Esta é uma mensagem automática, não é necessário responder por aqui.*`;

              // 1) Enviar pesquisa CSAT (operator_message_to_client)
              const csatPayload = buildOperatorMessagePayload({
                userId: 'dd7ee778-1d0e-4bdd-bfbd-78021e18ebed',
                userEmail: null,
                userName: 'Bot',
                orgId: snapOrgId,
                orgName: null,
                instance: {
                  id: (csatInstance as any).id,
                  name: (csatInstance as any).name ?? (csatInstance as any).instance_name ?? null,
                  channel: resolvedChannel,
                  status: (csatInstance as any).status ?? null,
                },
                chat: {
                  chat_id: csatLinkId,
                  instance_id: snapInstanceId,
                  origem_contato: snapPhone || null,
                  phone: snapPhone || null,
                  org_id: snapOrgId,
                  channel: resolvedChannel,
                },
                contact: { phone: snapPhone || null, origem_contato: snapPhone || null },
                message: {
                  content_message: surveyMessage,
                  message_type: 'text',
                  created_at: closedAtIso || new Date().toISOString(),
                },
              });
              console.log('[CSAT] Enviando webhook (pesquisa)', csatPayload);
              const csatResult = await sendOperatorMessageToWebhook(csatPayload, { supabase });
              console.log('[CSAT] Webhook respondeu:', csatResult);

              // 2) Salvar mensagem interna de encerramento diretamente no banco
              // chat_id deve ser o UUID puro — remover prefixo "instanceId:" se presente
              const closingMsgChatId = snapChatId.includes(':')
                ? snapChatId.split(':').slice(1).join(':')
                : snapChatId;
              if (closingMsgChatId) {
                const { error: insertErr } = await chatSupabase.from('messages').insert({
                  chat_id: closingMsgChatId,
                  instance_id: snapInstanceId,
                  origem_contato: snapPhone || null,
                  content_message: `Chat encerrado. Motivo: ${[snapCloseReason, snapCloseSubreason].filter(Boolean).join(' – ')}`,
                  message_type: 'text',
                  user_sender: 'Sistema',
                  agent_id: snapUserId,
                  is_internal: true,
                  created_at: closedAtIso || new Date().toISOString(),
                });
                if (insertErr) console.warn('[CSAT] Erro ao salvar mensagem de encerramento:', insertErr);
              }
            }
          } catch (csatErr) {
            console.error('[CSAT] Erro ao enviar pesquisa:', csatErr);
          }
        })();
      }
    }
  };

  // Função para salvar campos customizados
  const saveCustomFields = async () => {
    if (!currentContact || !currentOrg?.id || !user) return;

    setSavingCustomFields(true);

    try {
      const normalizedPhone = normalizePhone(chat.phone);
      
      const CONTACT_FIELDS_SAVE = 'id, org_id, name, email, whatsapp, phone, notes, created_at, updated_at, instance_id, custom_fields, visitor_id, metadata, Account_ID_insta, User_name_insta';
      const { data: allContacts } = await supabase
        .from('contacts')
        .select(CONTACT_FIELDS_SAVE)
        .eq('org_id', currentOrg.id);
      const contact = allContacts?.find((c: any) => {
        const contactNormalized = normalizePhone(c.phone);
        return contactNormalized === normalizedPhone && contactNormalized.length > 0;
      });

      if (contact) {
        const { error } = await supabase
          .from('contacts')
          .update({
            custom_fields: customFields,
            updated_at: new Date().toISOString()
          })
          .eq('id', contact.id);

        if (error) throw error;

        setCurrentContact({ ...currentContact, custom_fields: customFields });
        toast.success('Campos customizados salvos com sucesso!');
        setShowAddField(false);
        setNewFieldName("");
        setNewFieldValue("");
        setEditingField(null);
      } else {
        toast.error('Contato não encontrado no banco de dados. Salve o contato primeiro para usar campos customizados.');
      }
    } catch (error) {
      console.error('Erro ao salvar campos customizados:', error);
      toast.error('Erro ao salvar campos customizados. Tente novamente.');
    } finally {
      setSavingCustomFields(false);
    }
  };

  // Função para adicionar novo campo
  const handleAddField = () => {
    if (!newFieldName.trim()) {
      toast.error('Digite o nome do campo.');
      return;
    }

    // Verificar se o campo já existe
    if (customFields[newFieldName.trim()] !== undefined) {
      toast.error('Este campo já existe. Use a opção de editar para modificá-lo.');
      return;
    }

    setCustomFields({
      ...customFields,
      [newFieldName.trim()]: newFieldValue.trim()
    });

    setNewFieldName("");
    setNewFieldValue("");
    setShowAddField(false);
    
    // Salvar automaticamente
    setTimeout(() => saveCustomFields(), 100);
  };

  // Função para editar campo existente
  const handleEditField = (fieldName: string) => {
    setEditingField(fieldName);
    setNewFieldName(fieldName);
    setNewFieldValue(customFields[fieldName] || "");
    setShowAddField(true);
  };

  // Função para salvar edição de campo
  const handleSaveEditField = () => {
    if (!editingField || !newFieldName.trim()) {
      toast.error('Digite o nome do campo.');
      return;
    }

    const updatedFields = { ...customFields };
    
    // Se o nome mudou, remover o antigo e adicionar o novo
    if (editingField !== newFieldName.trim()) {
      delete updatedFields[editingField];
    }
    
    updatedFields[newFieldName.trim()] = newFieldValue.trim();
    setCustomFields(updatedFields);

    setEditingField(null);
    setNewFieldName("");
    setNewFieldValue("");
    setShowAddField(false);
    
    // Salvar automaticamente
    setTimeout(() => saveCustomFields(), 100);
  };

  // Função para remover campo
  const handleRemoveField = (fieldName: string) => {
    const updatedFields = { ...customFields };
    delete updatedFields[fieldName];
    setCustomFields(updatedFields);
    
    // Salvar automaticamente
    setTimeout(() => saveCustomFields(), 100);
  };

  // Função para cancelar adição/edição
  const handleCancelField = () => {
    setShowAddField(false);
    setEditingField(null);
    setNewFieldName("");
    setNewFieldValue("");
  };

  return (
    <div className="px-3 md:px-6 py-3 md:py-4 flex flex-wrap items-center gap-x-3 gap-y-2 bg-white dark:bg-card border-b border-[#e2e8f0] dark:border-border" data-chat-header>
      {/* Perfil: Avatar + Nome + telefone — ocupa no mínimo 200px e cresce conforme espaço disponível */}
      <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1 min-w-[200px]">
        <div className="h-9 w-9 md:h-10 md:w-10 rounded-full overflow-hidden shrink-0">
          <img
            src={headerAvatarSrc}
            alt={`Avatar de ${chat.phone || "contato"}`}
            className="h-full w-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).src = getDefaultAvatar(chat.phone || chat.id);
            }}
          />
        </div>
        <div className="flex flex-col items-start min-w-0 flex-1">
          <div className="flex items-center gap-1 md:gap-2">
            <h3 className="font-semibold text-[15px] md:text-[18px] leading-5 md:leading-7 text-[#020817] dark:text-foreground truncate max-w-full">
              {displayName}
            </h3>
            <Button
              variant="ghost"
              size="sm"
              className="h-5 w-5 md:h-6 md:w-6 p-0 hover:bg-primary/10 rounded-[6px] shrink-0"
              onClick={() => { mixpanel.track('app_chats_contact_info_opened'); setShowContactInfo(true) }}
            >
              <Info className="h-3.5 w-3.5 md:h-4 md:w-4 text-[#64748b]" />
            </Button>
          </div>
          <div className="flex items-center gap-1.5 md:gap-2 flex-wrap">
            <p className="text-[13px] md:text-[14px] leading-5 text-[#64748b]">
              {isInstagramChannel && instagramUsername
                ? instagramUsername
                : (chat.phone && chat.phone !== currentContact?.name ? chat.phone : chat.instance?.instance_name)}
            </p>
            {contactTags && contactTags.length > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap">
                {contactTags.slice(0, 3).map((tag: any) => (
                  <Badge
                    key={tag.id}
                    variant="secondary"
                    className="text-[10px] px-1.5 py-0.5 rounded-full h-5 flex items-center gap-1"
                    style={{
                      backgroundColor: `${tag.color}15`,
                      color: tag.color,
                      borderColor: `${tag.color}30`,
                      borderWidth: '1px'
                    }}
                  >
                    <div
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ backgroundColor: tag.color }}
                    />
                    <span className="truncate max-w-[60px]">{tag.name}</span>
                  </Badge>
                ))}
                {contactTags.length > 3 && (
                  <Badge
                    variant="outline"
                    className="text-[10px] px-1.5 py-0.5 rounded-full h-5"
                  >
                    +{contactTags.length - 3}
                  </Badge>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Botões de ação — mantém tamanho natural e quebra para a linha seguinte quando necessário */}
      <div className="flex items-center gap-1.5 flex-wrap shrink-0">
        {/* Atribuir */}
        <Button
          size="sm"
          className="h-8 md:h-10 px-3 md:px-4 py-1.5 md:py-2.5 bg-[#eef1f4] hover:bg-[#e2e6ea] text-[#4a5565] text-xs md:text-sm font-medium rounded-xl border-0 shadow-[0px_1px_0.5px_0px_rgba(29,41,61,0.02)] gap-1.5"
          onClick={() => { mixpanel.track('app_chats_assign_dialog_opened'); setAssignDialogOpen(true) }}
          disabled={loadingAssignment}
        >
          <UserPlus className="h-3.5 w-3.5 md:h-4 md:w-4" />
          Atribuir
        </Button>

        {/* Tags */}
        {currentContact && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" className="h-8 md:h-10 px-3 md:px-4 py-1.5 md:py-2.5 bg-[#eef1f4] hover:bg-[#e2e6ea] text-[#4a5565] text-xs md:text-sm font-medium rounded-xl border-0 shadow-[0px_1px_0.5px_0px_rgba(29,41,61,0.02)] gap-1.5">
                <TagIcon className="h-3.5 w-3.5 md:h-4 md:w-4" />
                Tags
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <div className="p-2">
                <div className="text-sm font-medium mb-2">Atribuir Tags</div>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {tags?.map((tag) => {
                    const isAssigned = contactTags.some(ct => ct.id === tag.id);
                    return (
                      <div key={tag.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={tag.id}
                          checked={isAssigned}
                          onCheckedChange={(checked) => handleTagToggle(tag.id, checked as boolean)}
                        />
                        <div className="flex items-center gap-2 flex-1">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: tag.color }}
                          />
                          <label
                            htmlFor={tag.id}
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer flex-1"
                          >
                            {tag.name}
                          </label>
                        </div>
                      </div>
                    );
                  })}
                  {tags?.length === 0 && (
                    <div className="text-center py-4">
                      <TagIcon className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">
                        Nenhuma tag disponível. Crie tags primeiro.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        
        {/* Histórico */}
        <Button
          size="sm"
          className="h-8 md:h-10 px-3 md:px-4 py-1.5 md:py-2.5 bg-[#eef1f4] hover:bg-[#e2e6ea] text-[#4a5565] text-xs md:text-sm font-medium rounded-xl border-0 shadow-[0px_1px_0.5px_0px_rgba(29,41,61,0.02)] gap-1.5"
          onClick={() => { mixpanel.track('app_chats_history_toggled', { show: !showAllMessages }); onLoadFullHistory && onLoadFullHistory() }}
        >
          <History className="h-3.5 w-3.5 md:h-4 md:w-4" />
          Histórico
        </Button>

        {/* Botão de Encerrar Chat */}
        {((assignedToId &&
         !(chat as any).closed_at && !(chat as any).full?.closed_at &&
         (chat as any)?.status_atd !== 'Chat Encerrado' && (currentContact as any)?.statusatd !== 'Chat Encerrado') ||
         (currentContact &&
         (!currentContact.closed_at || hasNewMessagesAfterClose) &&
         ((chat as any)?.status_atd !== 'chat finalizado' && (chat as any)?.status_atd !== 'Chat Encerrado' && (currentContact as any)?.statusatd !== 'chat finalizado' && (currentContact as any)?.statusatd !== 'Chat Encerrado')) ||
         (isInstagramChannel && onCloseChatInstagram && !(chat as any).closed_at && !(chat as any).full?.closed_at)) && (
          <Button
            size="sm"
            onClick={() => { mixpanel.track('app_chats_close_dialog_opened'); setShowCloseDialog(true) }}
            className="h-8 md:h-10 px-3 md:px-4 py-1.5 md:py-2.5 bg-transparent border border-[#c70036] text-[#c70036] text-xs md:text-sm font-medium rounded-xl shadow-[0px_1px_0.5px_0px_rgba(29,41,61,0.02)] hover:bg-[#c70036]/10 gap-1.5"
            data-end-chat-button
          >
            <XCircle className="h-3.5 w-3.5 md:h-4 md:w-4" />
            Encerrar
          </Button>
        )}
      </div>

      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Atribuir chat</DialogTitle>
            <DialogDescription>
              Atribua para você ou escolha outro usuário da organização.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Button
              variant="secondary"
              className="w-full justify-start gap-2"
              onClick={() => {
                mixpanel.track('app_chats_assign_to_me')
                setAssignDialogOpen(false);
                handleAssignToMe();
              }}
              disabled={loadingAssignment || assignedToMe}
            >
              <UserPlus className="h-4 w-4" />
              Atribuir para mim
            </Button>
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase text-muted-foreground">
                <span className="bg-background px-2">ou</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Atribuir para outro usuário</Label>
              <Select
                value={selectedAssigneeId}
                onValueChange={setSelectedAssigneeId}
                disabled={loadingMembers}
              >
                <SelectTrigger>
                  <SelectValue placeholder={loadingMembers ? "Carregando..." : "Selecione um usuário"} />
                </SelectTrigger>
                <SelectContent>
                  {orgMembers.map((member) => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.name || member.email || "Usuário"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                className="w-full"
                onClick={() => {
                  mixpanel.track('app_chats_assign_to_user')
                  handleAssignToUser(selectedAssigneeId);
                  setAssignDialogOpen(false);
                }}
                disabled={loadingAssignment || !selectedAssigneeId}
              >
                Atribuir
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAssignDialogOpen(false)}
              disabled={loadingAssignment}
            >
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Dialog de informações do cliente */}
      <Dialog open={showContactInfo} onOpenChange={(open) => {
        setShowContactInfo(open);
        if (!open) {
          setIsEditingBasicInfo(false);
          setEditingName("");
          setEditingWhatsApp("");
          setEditingPhone("");
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Informações do Cliente</DialogTitle>
            <DialogDescription>
              Dados cadastrais e informações de atendimento
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* Informações Básicas */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  Informações Básicas
                </h4>
                {!isEditingBasicInfo ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setIsEditingBasicInfo(true);
                      const name = contactForDisplay?.name ?? (chat as any).contactName ?? (chat as any).full?.name ?? chat.phone ?? "";
                      const whatsapp = contactForDisplay?.whatsapp ?? chat.phone ?? "";
                      setEditingName(name);
                      setEditingWhatsApp(whatsapp);
                      setEditingPhone(chat.phone ?? "");
                    }}
                    className="h-8"
                  >
                    <Edit2 className="h-4 w-4 mr-2" />
                    Editar
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setIsEditingBasicInfo(false);
                        setEditingName("");
                        setEditingWhatsApp("");
                        setEditingPhone("");
                      }}
                      className="h-8"
                      disabled={savingBasicInfo}
                    >
                      Cancelar
                    </Button>
                    <Button
                      size="sm"
                      onClick={async () => {
                        if (!currentOrg?.id) {
                          toast.error('Organização não encontrada');
                          return;
                        }
                        
                        if (!currentContact?.id) {
                          // Validar telefone antes de criar/buscar
                          const normalizedPhone = normalizePhone(editingPhone);
                          const normalizedWhatsApp = normalizePhone(editingWhatsApp);
                          if (!normalizedPhone || normalizedPhone.length < 10) {
                            toast.error('Informe um telefone válido para criar o contato.');
                            return;
                          }
                          try {
                            const CONTACT_FIELDS_RET = 'id, org_id, name, email, whatsapp, phone, notes, created_at, updated_at, instance_id, custom_fields, visitor_id, metadata, Account_ID_insta, User_name_insta';
                            const { data: existingContacts } = await supabase
                              .from('contacts')
                              .select('id, phone')
                              .eq('org_id', currentOrg.id);
                            const existing = existingContacts?.find(
                              (c) => normalizePhone(c.phone || '') === normalizedPhone
                            );
                            if (existing) {
                              const now = new Date().toISOString();
                              const updateData: any = {
                                name: editingName.trim() || normalizedPhone,
                                phone: normalizedPhone,
                                whatsapp: normalizedWhatsApp || normalizedPhone,
                                updated_at: now,
                              };
                              const { data: updatedContact, error: updateError } = await supabase
                                .from('contacts')
                                .update(updateData)
                                .eq('id', existing.id)
                                .select(CONTACT_FIELDS_RET)
                                .single();
                              if (updateError) throw updateError;
                              setCurrentContact(updatedContact);
                              setIsEditingBasicInfo(false);
                              toast.success('Contato já existia; dados atualizados.');
                              return;
                            }
                            const now = new Date().toISOString();
                            const newContactData: any = {
                              org_id: currentOrg.id,
                              name: editingName.trim() || normalizedPhone,
                              phone: normalizedPhone,
                              whatsapp: normalizedWhatsApp || normalizedPhone,
                              created_at: now,
                              updated_at: now,
                            };
                            const { data: newContact, error: createError } = await supabase
                              .from('contacts')
                              .insert(newContactData)
                              .select(CONTACT_FIELDS_RET)
                              .single();
                            if (createError) throw createError;
                            setCurrentContact(newContact);
                            setIsEditingBasicInfo(false);
                            toast.success('Contato criado com sucesso!');
                            return;
                          } catch (error: any) {
                            console.error('Erro ao criar contato:', error);
                            toast.error('Erro ao criar contato. Tente novamente.');
                            return;
                          }
                        }
                        
                        setSavingBasicInfo(true);
                        try {
                          // Validar campos obrigatórios
                          if (!editingPhone || editingPhone.trim().length === 0) {
                            toast.error('O telefone é obrigatório');
                            setSavingBasicInfo(false);
                            return;
                          }
                          
                          const normalizedPhone = normalizePhone(editingPhone);
                          if (!normalizedPhone || normalizedPhone.length < 10) {
                            toast.error('Telefone inválido');
                            setSavingBasicInfo(false);
                            return;
                          }
                          
                          const normalizedWhatsApp = normalizePhone(editingWhatsApp);
                          
                          // Preparar dados para atualização
                          const updateData: any = {
                            name: editingName.trim() || normalizedPhone,
                            phone: normalizedPhone,
                            whatsapp: normalizedWhatsApp || normalizedPhone,
                            updated_at: new Date().toISOString(),
                          };
                          
                          console.log('Atualizando contato no banco:', {
                            contactId: currentContact.id,
                            updateData,
                          });
                          
                          // Atualizar no banco de dados
                          const CONTACT_FIELDS_RET = 'id, org_id, name, email, whatsapp, phone, notes, created_at, updated_at, instance_id, custom_fields, visitor_id, metadata, Account_ID_insta, User_name_insta';
                          const { data: updatedContact, error } = await supabase
                            .from('contacts')
                            .update(updateData)
                            .eq('id', currentContact.id)
                            .select(CONTACT_FIELDS_RET)
                            .single();
                          
                          if (error) {
                            console.error('Erro ao atualizar contato:', error);
                            throw error;
                          }
                          
                          if (!updatedContact) {
                            throw new Error('Nenhum registro foi atualizado');
                          }
                          
                          console.log('Contato atualizado com sucesso:', updatedContact);
                          
                          // Atualizar estado local com os dados retornados do banco
                          setCurrentContact(updatedContact);
                          
                          setIsEditingBasicInfo(false);
                          toast.success('Informações atualizadas com sucesso no banco de dados!');
                          
                          // Recarregar dados do contato para garantir sincronização
                          const normalizedChatPhone = normalizePhone(chat.phone);
                          const CONTACT_FIELDS_RELOAD = 'id, org_id, name, email, whatsapp, phone, notes, created_at, updated_at, instance_id, custom_fields, visitor_id, metadata, Account_ID_insta, User_name_insta';
                          const { data: allContacts } = await supabase
                            .from('contacts')
                            .select(CONTACT_FIELDS_RELOAD)
                            .eq('org_id', currentOrg.id);
                          
                          const contact = allContacts?.find((c: any) => {
                            const contactNormalized = normalizePhone(c.phone);
                            return contactNormalized === normalizedChatPhone && contactNormalized.length > 0;
                          });
                          
                          if (contact) {
                            setCurrentContact(contact);
                          }
                        } catch (error: any) {
                          console.error('Erro ao atualizar informações no banco:', error);
                          toast.error(`Erro ao atualizar informações: ${error.message || 'Tente novamente'}`);
                        } finally {
                          setSavingBasicInfo(false);
                        }
                      }}
                      className="h-8"
                      disabled={savingBasicInfo}
                    >
                      {savingBasicInfo ? 'Salvando...' : 'Salvar'}
                    </Button>
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <User className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div className="flex-1">
                    <Label className="text-sm font-medium text-muted-foreground">Nome</Label>
                    {isEditingBasicInfo ? (
                      <Input
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        placeholder="Nome do cliente"
                        className="mt-1"
                        disabled={savingBasicInfo}
                      />
                    ) : (
                      <p className="text-sm text-foreground mt-1">
                        {contactForDisplay?.name ?? (chat as any).contactName ?? (chat as any).full?.name ?? chat.phone ?? "Não informado"}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Phone className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div className="flex-1">
                    <Label className="text-sm font-medium text-muted-foreground">Telefone</Label>
                    {isEditingBasicInfo ? (
                      <Input
                        value={editingPhone}
                        onChange={(e) => setEditingPhone(e.target.value)}
                        placeholder="Telefone"
                        className="mt-1"
                        disabled={savingBasicInfo}
                      />
                    ) : (
                      <p className="text-sm text-foreground mt-1">
                        {contactForDisplay?.phone ?? chat.phone ?? (isInstagramChannel ? "Sem número (Instagram)" : "Não informado")}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <MessageSquare className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div className="flex-1">
                    <Label className="text-sm font-medium text-muted-foreground">WhatsApp</Label>
                    {isEditingBasicInfo ? (
                      <Input
                        value={editingWhatsApp}
                        onChange={(e) => setEditingWhatsApp(e.target.value)}
                        placeholder="WhatsApp"
                        className="mt-1"
                        disabled={savingBasicInfo}
                      />
                    ) : (
                      <p className="text-sm text-foreground mt-1">
                        {contactForDisplay?.whatsapp ?? chat.phone ?? (isInstagramChannel ? "Sem número (Instagram)" : "Não informado")}
                      </p>
                    )}
                  </div>
                </div>
                
                {contactForDisplay?.email && (
                  <div className="flex items-start gap-3">
                    <Mail className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">E-mail</p>
                      <p className="text-sm text-foreground">{contactForDisplay.email}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Informações de Atendimento */}
            <div className="space-y-4">
              <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                Informações de Atendimento
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <UserPlus className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Status</p>
                    <Badge 
                      variant="secondary" 
                      className="mt-1"
                    >
                      {((chat as any)?.status_atd ?? contactForDisplay?.statusatd) === 'fila humana' 
                        ? 'Não Designado' 
                        : ((chat as any)?.status_atd ?? contactForDisplay?.statusatd) ?? 'Não informado'}
                    </Badge>
                  </div>
                </div>
                
                {assignedUserName && (
                  <div className="flex items-start gap-3">
                    <User className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Atribuído para</p>
                      <p className="text-sm text-foreground">{assignedUserName}</p>
                    </div>
                  </div>
                )}
                
                {contactForDisplay?.last_interaction_at && (
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Última Interação</p>
                      <p className="text-sm text-foreground">
                        {format(new Date(contactForDisplay.last_interaction_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                      </p>
                    </div>
                  </div>
                )}
                
                {contactForDisplay?.created_at && (
                  <div className="flex items-start gap-3">
                    <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Cadastrado em</p>
                      <p className="text-sm text-foreground">
                        {format(new Date(contactForDisplay.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Informações do Instagram */}
            {((contactForDisplay as any)?.Account_ID_insta ?? (contactForDisplay as any)?.account_id_insta ?? (contactForDisplay as any)?.User_name_insta ?? (contactForDisplay as any)?.user_name_insta ?? (chat as any).contactHandle) && (
              <div className="space-y-4">
                <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  Informações do Instagram
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {((contactForDisplay as any)?.Account_ID_insta ?? (contactForDisplay as any)?.account_id_insta ?? chat.phone) && (
                    <div className="flex items-start gap-3">
                      <Instagram className="h-5 w-5 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">ID da Conta</p>
                        <p className="text-sm text-foreground">{(contactForDisplay as any)?.Account_ID_insta ?? (contactForDisplay as any)?.account_id_insta ?? chat.phone}</p>
                      </div>
                    </div>
                  )}
                  
                  {((contactForDisplay as any)?.User_name_insta ?? (contactForDisplay as any)?.user_name_insta ?? (chat as any).contactHandle) && (
                    <div className="flex items-start gap-3">
                      <Instagram className="h-5 w-5 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Nome de Usuário</p>
                        <p className="text-sm text-foreground">
                          {(() => {
                            const u = (contactForDisplay as any)?.User_name_insta ?? (contactForDisplay as any)?.user_name_insta ?? (chat as any).contactHandle ?? '';
                            return u && String(u).startsWith('@') ? String(u) : `@${String(u).replace(/^@/, '')}`;
                          })()}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Tags */}
            {contactTags && contactTags.length > 0 && (
              <div className="space-y-4">
                <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  Tags
                </h4>
                <div className="flex flex-wrap gap-2">
                  {contactTags.map((tag: any) => (
                    <Badge
                      key={tag.id}
                      variant="secondary"
                      className="text-xs px-2 py-1"
                      style={{
                        backgroundColor: `${tag.color}15`,
                        color: tag.color,
                        borderColor: `${tag.color}30`,
                        borderWidth: '1px'
                      }}
                    >
                      <div
                        className="w-1.5 h-1.5 rounded-full mr-1.5"
                        style={{ backgroundColor: tag.color }}
                      />
                      {tag.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            {/* Observações */}
            {contactForDisplay?.notes && (
              <div className="space-y-4">
                <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  Observações
                </h4>
                <div className="flex items-start gap-3">
                  <FileText className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <p className="text-sm text-foreground whitespace-pre-wrap">
                    {contactForDisplay.notes}
                  </p>
                </div>
              </div>
            )}
            
            {/* Campos Customizados */}
            {currentContact && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                    Campos Customizados
                  </h4>
                  {!showAddField && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setShowAddField(true);
                        setEditingField(null);
                        setNewFieldName("");
                        setNewFieldValue("");
                      }}
                      className="h-8"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Adicionar Campo
                    </Button>
                  )}
                </div>

                {/* Formulário para adicionar/editar campo */}
                {showAddField && (
                  <div className="p-4 border rounded-lg bg-muted/50 space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor="field-name">Nome do Campo *</Label>
                      <Input
                        id="field-name"
                        placeholder="Ex: CPF, Endereço, Empresa..."
                        value={newFieldName}
                        onChange={(e) => setNewFieldName(e.target.value)}
                        disabled={savingCustomFields || !!editingField}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="field-value">Valor</Label>
                      <Textarea
                        id="field-value"
                        placeholder="Digite o valor do campo..."
                        value={newFieldValue}
                        onChange={(e) => setNewFieldValue(e.target.value)}
                        className="min-h-[80px]"
                        disabled={savingCustomFields}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={editingField ? handleSaveEditField : handleAddField}
                        disabled={!newFieldName.trim() || savingCustomFields}
                      >
                        {editingField ? 'Salvar Alterações' : 'Adicionar'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCancelField}
                        disabled={savingCustomFields}
                      >
                        Cancelar
                      </Button>
                    </div>
                  </div>
                )}

                {/* Lista de campos customizados */}
                {Object.keys(customFields).length > 0 ? (
                  <div className="space-y-3">
                    {Object.entries(customFields).map(([fieldName, fieldValue]) => (
                      <div
                        key={fieldName}
                        className="flex items-start gap-3 p-3 border rounded-lg bg-background hover:bg-muted/50 transition-colors"
                      >
                        <FileText className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <p className="text-sm font-medium text-foreground">
                              {fieldName}
                            </p>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditField(fieldName)}
                                className="h-7 w-7 p-0"
                                disabled={savingCustomFields || showAddField}
                              >
                                <Edit2 className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveField(fieldName)}
                                className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                                disabled={savingCustomFields || showAddField}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                          <p className="text-sm text-muted-foreground whitespace-pre-wrap break-words">
                            {fieldValue || <span className="italic">Sem valor</span>}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : !showAddField && (
                  <div className="text-center py-6 border border-dashed rounded-lg">
                    <FileText className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">
                      Nenhum campo customizado adicionado ainda.
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Clique em "Adicionar Campo" para criar um novo campo.
                    </p>
                  </div>
                )}
              </div>
            )}
            
            {/* Mensagem quando não há dados */}
            {!contactForDisplay && (
              <div className="text-center py-8">
                <p className="text-sm text-muted-foreground">
                  Nenhuma informação cadastral disponível para este contato.
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de Encerrar Chat */}
      <Dialog open={showCloseDialog} onOpenChange={setShowCloseDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Encerrar Chat</DialogTitle>
            <DialogDescription>
              Preencha o motivo e submotivo para encerrar este chat. Após o encerramento, as mensagens anteriores não serão exibidas automaticamente, a menos que o histórico completo seja visualizado.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="close-reason">Motivo *</Label>
              <Textarea
                id="close-reason"
                placeholder="Digite o motivo do encerramento..."
                value={closeReason}
                onChange={(e) => setCloseReason(e.target.value)}
                className="min-h-[80px]"
                disabled={closingChat}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="close-subreason">Submotivo *</Label>
              <Textarea
                id="close-subreason"
                placeholder="Digite o submotivo do encerramento..."
                value={closeSubreason}
                onChange={(e) => setCloseSubreason(e.target.value)}
                className="min-h-[80px]"
                disabled={closingChat}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCloseDialog(false);
                setCloseReason("");
                setCloseSubreason("");
              }}
              disabled={closingChat}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleCloseChat}
              disabled={!closeReason.trim() || !closeSubreason.trim() || closingChat}
              className="bg-destructive hover:bg-destructive/90"
            >
              {closingChat ? "Encerrando..." : "Encerrar Chat"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Função de comparação customizada para evitar re-renders desnecessários
const areEqual = (prevProps: ChatHeaderProps, nextProps: ChatHeaderProps) => {
  const prev = prevProps.chat as any;
  const next = nextProps.chat as any;
  return (
    prevProps.chat?.id === nextProps.chat?.id &&
    prevProps.chat?.phone === nextProps.chat?.phone &&
    prevProps.chat?.updated_at === nextProps.chat?.updated_at &&
    (prev?.contactName ?? prev?.full?.name) === (next?.contactName ?? next?.full?.name) &&
    prevProps.showAllMessages === nextProps.showAllMessages &&
    prevProps.contactTags?.length === nextProps.contactTags?.length &&
    prevProps.contactTags?.every((tag, index) => 
      nextProps.contactTags?.[index]?.id === tag?.id
    )
  );
};

export const ChatHeader = React.memo(ChatHeaderComponent, areEqual);

